<!--
  - Copyright (c) 2008-2020 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
  - This is CONFIDENTIAL code. Use is subject to license terms.
  -->
<template>
  <div>
    <!-- Login or predefined properties -->
    <div v-if="!nameEditable" :class="type === 'BOOLEAN' ? 'mt-3' : 'mt-4'">
      <div v-if="type !== 'BOOLEAN'" class="d-flex align-items-center mt-4">
        <text-input
          :id="name"
          :value="value"
          :label="nameLabel"
          :type="inputType"
          @input="$emit('updatePropertyValue', $event)"
          :disabled="disabled"
          :readonly="disabled"
          encoding="none"
          :autocomplete="masked ? 'new-password' : 'off'"
          class="flex-grow-1"
        >
          <tooltip-icon v-if="$te(`tooltip.${name}`)" :text="$t(`tooltip.${name}`)" :html="true"></tooltip-icon>
          <icon
            v-if="autoGenerated"
            :icon="['fas', 'bolt-auto']"
            v-tooltip="$t('tooltip.autoGenerated', { chars: autoGeneratedLength })"
            class="text-primary cursor-pointer"
          />
        </text-input>
        <div class="btn-group ml-2" role="group">
          <div v-if="masked" class="btn-group" role="group">
            <button class="btn btn-outline-primary p-2" @click.prevent="isMaskRevealed = !isMaskRevealed">
              <icon v-if="isMaskRevealed" :icon="['fas', 'eye-slash']"></icon>
              <icon v-else :icon="['fas', 'eye']"></icon>
            </button>
          </div>
          <button
            v-if="addCopyButton"
            class="btn btn-outline-primary p-2"
            @click.prevent="copyText(value)"
            :class="{
              'border-left-0': masked,
            }"
          >
            <icon :icon="['fas', 'copy']"></icon>
          </button>
        </div>
      </div>

      <!-- Boolean value -->
      <basic-label-toggle
        v-else
        :value="value"
        :lbl="nameLabel"
        :lbl-after="false"
        add-class="justify-content-between w-100"
        @input="$emit('updatePropertyValue', $event)"
        :disabled="disabled"
      ></basic-label-toggle>
    </div>

    <!-- Custom properties -->
    <adjustable-type-input
      v-else
      :id="name"
      :disabled="disabled"
      v-bind="$props"
      @updateAttributeName="$emit('updatePropertyName', $event)"
      @updateAttributeValue="$emit('updatePropertyValue', $event)"
      class="mt-2"
    >
      <template #after-attribute-region>
        <div>
          <button
            class="btn btn-secondary btn-sm p-2 ml-2"
            type="button"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
            :disabled="disabled"
          >
            <icon :icon="['fas', 'caret-down']"></icon>
          </button>
          <div class="dropdown-menu dropdown-menu-right dropdown-type-switch">
            <div
              class="dropdown-item"
              v-for="type in ['string', 'number', 'boolean']"
              :key="type"
              @click="$emit('updatePropertyType', type)"
            >
              {{ $t(`userPropertyType.${type}`) }}
            </div>
          </div>
        </div>
      </template>
    </adjustable-type-input>
  </div>
</template>

<script>
import TextInput from "@/components/TextInput"
import AdjustableTypeInput from "@/components/AdjustableTypeInput"
import BasicLabelToggle from "../BasicLabelToggle"
import TooltipIcon from "./TooltipIcon"

export default {
  name: "UserProperty",

  components: { TooltipIcon, BasicLabelToggle, TextInput, AdjustableTypeInput },

  props: {
    name: { type: String, required: true },
    value: { type: [String, Number, Boolean], required: true },
    type: { type: String, required: true },
    nameEditable: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    autoGenerated: { type: Boolean, default: false },
    autoGeneratedLength: { type: Number, default: 0 },
    masked: { type: Boolean, default: false },
    addCopyButton: { type: Boolean, default: false },
  },

  data() {
    return {
      isMaskRevealed: false,
    }
  },

  computed: {
    nameLabel() {
      let propertyLblKey = `label.${this.name}`
      if (this.$te(propertyLblKey)) {
        return this.$t(propertyLblKey)
      }
      return this.name
    },

    inputType() {
      if (this.masked && !this.isMaskRevealed) return "password"
      return this.type === "STRING" ? "text" : "number"
    },
  },

  methods: {
    async copyText(text) {
      await navigator.clipboard.writeText(text)
    },
  },

  i18n: {
    messages: {
      de: {
        label: {
          title: "Anrede <i>(title)</i>",
          firstname: "Vorname <i>(firstname)</i>",
          lastname: "Nachname <i>(lastname)</i>",
          displayname: "Name <i>(displayname)</i>",
          email: "Email-Adresse <i>(email)</i>",
          locale: "Sprache <i>(locale)</i>",
          votingweight: "Stimmgewicht <i>(votingweight)</i>",
          skipwaitingroom: "Warteraum überspringen <i>(skipwaitingroom)</i>",
          favorite: "Favorit <i>(favorite)</i>",
          accredited: "Akkreditiert <i>(accredited)</i>",
        },
        tooltip: {
          displayname: "Wird automatisch generiert, wenn Vorname und/oder Nachname gesetzt ist.",
          votingweight:
            "Ganzzahliger Wert. Ist ein Stimmgewicht größer 1 gesetzt, wird bei Abstimmungen die entsprechende Anzahl Beiträge abgeschickt.",
          autoGenerated: "Wenn Sie dieses Feld leer lassen, wird der Wert automatisch generiert ({chars} Zeichen).",
        },
        autoGenerated: "(auto-generiert)",
      },
      en: {
        label: {
          title: "Title <i>(title)</i>",
          firstname: "First name <i>(firstname)</i>",
          lastname: "Last name <i>(lastname)</i>",
          displayname: "Name <i>(displayname)</i>",
          email: "Email address <i>(email)</i>",
          locale: "Language <i>(locale)</i>",
          votingweight: "Voting weight <i>(votingweight)</i>",
          skipwaitingroom: "Skip waiting room <i>(skipwaitingroom)</i>",
          favorite: "Favorite <i>(favorite)</i>",
          accredited: "Accredited <i>(accredited)</i>",
        },
        tooltip: {
          displayname: "Automatically generated if first name and/or last name is set.",
          votingweight:
            "Integer value. If a voting weight greater than 1 is set, the corresponding number of contributions will be submitted during votings.",
          autoGenerated: "If you leave this empty, the value will be automatically generated ({chars} characters).",
        },
        autoGenerated: "(auto-generated)",
      },
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss_custom/custom-variables.scss";

.dropdown-type-switch {
  min-width: 0;
}

.min-w-50 {
  min-width: 50%;
}

.btn-group .btn svg {
  width: 1em;
}

.btn-group .btn {
  border-right: 0;
}

/* Restore the border only for the last button */
.btn-group .btn:last-child {
  border-right: 1px solid currentColor;
}
</style>
